/**
 * PowerPoint embed URL utilities.
 *
 * Validation applies only when teachers save new or edited embed sources.
 * Saved URLs in Firestore are rendered as-is without validation or mutation.
 */

export const POWERPOINT_LOAD_TIMEOUT_MS = 15000;

/** Representative production URLs — used by regression tests only. */
export const LEGACY_POWERPOINT_URL_FIXTURES = [
  "https://view.officeapps.live.com/op/embed.aspx?src=https%3A%2F%2Fcontoso-my.sharepoint.com%2Fpersonal%2Fteacher_contoso_com%2FDocuments%2Flesson.pptx&wdEmbedCode=0",
  "https://view.officeapps.live.com/op/embed.aspx?src=https%3A%2F%2Fcontoso.sharepoint.com%2Fsites%2Fclassroom%2FShared%20Documents%2Fslides.pptx",
  "https://onedrive.live.com/embed?cid=ABCDEF123456&resid=ABCDEF123456%21789&authkey=ABC123&em=2",
  "https://contoso-my.sharepoint.com/personal/teacher_contoso_com/_layouts/15/embed.aspx?UniqueId=abc-123&embed=%7B%22...%22%7D",
  "https://contoso.sharepoint.com/sites/classroom/_layouts/15/embed.aspx?UniqueId=def-456",
  // Legacy URLs produced by the previous Google Slides suffix bug — must keep working.
  "https://view.officeapps.live.com/op/embed.aspx?src=https%3A%2F%2Fexample.com%2Fdeck.pptx&action=embedview&wdbipreview=true",
];

/** Modern Microsoft embed shapes teachers commonly paste (school OneDrive / SharePoint). */
export const MODERN_POWERPOINT_URL_FIXTURES = [
  "https://jmcss-my.sharepoint.com/personal/teacher_jmcss_org/_layouts/15/Doc.aspx?sourcedoc=%7Babc-123%7D&action=embedview&wdAr=1.777",
  "https://contoso-my.sharepoint.com/personal/user_contoso_com/_layouts/15/guestaccess.aspx?docid=abc&authkey=!xyz&action=embedview",
  "https://contoso.sharepoint.com/:p:/r/sites/classroom/Shared%20Documents/lesson.pptx?embed=1&action=embedview",
  "https://www.onedrive.com/embed?cid=ABCDEF&resid=ABCDEF%21789&authkey=ABC123",
];

const IFRAME_SRC_REGEX = /<iframe[^>]*\ssrc=["']([^"']+)["']/i;

const REJECTED_HOST_PATTERNS = [
  /(^|\.)docs\.google\.com$/i,
  /(^|\.)youtube\.com$/i,
  /(^|\.)youtu\.be$/i,
];

function decodeEmbedSrc(rawSrc) {
  if (!rawSrc) {
    return null;
  }

  return rawSrc.replace(/&amp;/g, "&").trim();
}

/**
 * Extract an embed URL from iframe markup or a bare URL string.
 * Does not mutate or validate the result.
 */
export function extractPowerpointEmbedUrl(input) {
  if (!input || typeof input !== "string") {
    return null;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  const iframeMatch = trimmed.match(IFRAME_SRC_REGEX);
  if (iframeMatch) {
    return decodeEmbedSrc(iframeMatch[1]);
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return null;
}

function parseHttpUrl(url) {
  try {
    const baseOrigin =
      typeof window !== "undefined" ? window.location.origin : "https://example.com";
    return new URL(url, baseOrigin);
  } catch {
    return null;
  }
}

function normalizeUrlForComparison(url) {
  const parsed = parseHttpUrl(url);
  if (!parsed) {
    return String(url || "").trim();
  }

  return parsed.toString();
}

function getSearchParamLower(parsed, name) {
  const value = parsed.searchParams.get(name);
  return value == null ? null : String(value).toLowerCase();
}

function hasEmbedViewAction(parsed) {
  return getSearchParamLower(parsed, "action") === "embedview";
}

function hasExplicitEmbedMarker(parsed) {
  if (hasEmbedViewAction(parsed)) {
    return true;
  }

  const embedParam = getSearchParamLower(parsed, "embed");
  if (embedParam === "1" || embedParam === "true") {
    return true;
  }

  // SharePoint sometimes uses embed=<json encoded options>
  if (embedParam && embedParam !== "0" && embedParam !== "false") {
    return true;
  }

  return false;
}

function isOneDriveConsumerEmbedHost(host) {
  return host === "onedrive.live.com" || host === "onedrive.com" || host === "www.onedrive.com";
}

function isOneDriveShortLinkHost(host) {
  return host === "1drv.ms" || host === "www.1drv.ms";
}

function decodeRedeemToUrl(redeemValue) {
  if (!redeemValue || typeof redeemValue !== "string") {
    return null;
  }

  try {
    const normalized = redeemValue.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    let decoded = "";
    if (typeof globalThis.atob === "function") {
      decoded = globalThis.atob(padded);
    } else if (typeof Buffer !== "undefined") {
      decoded = Buffer.from(padded, "base64").toString("utf8");
    } else {
      return null;
    }
    if (!/^https?:\/\//i.test(decoded)) {
      return null;
    }
    return decoded.trim();
  } catch {
    return null;
  }
}

/**
 * Personal OneDrive "Embed" dialogs now often emit 1drv.ms short links
 * (e.g. https://1drv.ms/p/c/{cid}/{shareToken}?em=2) instead of onedrive.live.com/embed.
 * Keep these as-is: the browser follows 1drv.ms → onedrive.live.com inside the iframe.
 * Wrapping them in view.officeapps.live.com/op/embed.aspx?src= causes Microsoft's
 * "File Not Found" viewer error for modern /p/c/ share tokens.
 */
function isConvertibleOneDrivePowerpointShortUrl(parsed) {
  if (!parsed || !isOneDriveShortLinkHost(parsed.hostname.toLowerCase())) {
    return false;
  }

  const segments = parsed.pathname.split("/").filter(Boolean);
  if (segments.length < 2 || segments[0].toLowerCase() !== "p") {
    return false;
  }

  // Modern personal embed / share shape: /p/c/{cid}/{shareToken}
  if (segments[1].toLowerCase() === "c") {
    return segments.length >= 4 && Boolean(segments[2]) && Boolean(segments[3]);
  }

  // Older share-token forms: /p/s!… or /p/{token}
  return segments[1].length > 4;
}

function isOneDriveEditOrRedirPath(path) {
  return (
    path === "/edit" ||
    path === "/edit.aspx" ||
    path.startsWith("/edit/") ||
    path === "/redir" ||
    path === "/redir.aspx"
  );
}

function looksLikePowerpointOneDriveEditUrl(parsed) {
  if (!parsed || !isOneDriveConsumerEmbedHost(parsed.hostname.toLowerCase())) {
    return false;
  }

  if (!isOneDriveEditOrRedirPath(parsed.pathname.toLowerCase())) {
    return false;
  }

  const ithint = String(parsed.searchParams.get("ithint") || "").toLowerCase();
  if (ithint.includes("pptx") || ithint.includes("ppt")) {
    return true;
  }

  // Embed intent from Microsoft short-link redirects
  if (hasExplicitEmbedMarker(parsed) && parsed.searchParams.has("resid")) {
    return true;
  }

  // redeem tokens from PowerPoint short links decode to 1drv.ms/p/...
  const redeemed = decodeRedeemToUrl(parsed.searchParams.get("redeem"));
  if (redeemed) {
    const redeemedParsed = parseHttpUrl(redeemed);
    if (isConvertibleOneDrivePowerpointShortUrl(redeemedParsed)) {
      return true;
    }
  }

  return false;
}

function buildOneDriveEmbedFromEditUrl(parsed) {
  const resid = parsed.searchParams.get("resid") || parsed.searchParams.get("id");
  if (!resid) {
    return null;
  }

  const embed = new URL("https://onedrive.live.com/embed");
  const cid = parsed.searchParams.get("cid");
  if (cid) {
    embed.searchParams.set("cid", cid);
  }
  embed.searchParams.set("resid", resid);
  embed.searchParams.set("em", "2");

  const redeem = parsed.searchParams.get("redeem");
  if (redeem) {
    embed.searchParams.set("redeem", redeem);
  }

  if (parsed.searchParams.get("migratedtospo")) {
    embed.searchParams.set("migratedtospo", parsed.searchParams.get("migratedtospo"));
  }

  return embed.toString();
}

/**
 * Older Launchpad builds wrapped 1drv.ms PowerPoint embeds in Office Online ?src=.
 * That produces File Not Found for modern personal OneDrive share tokens — unwrap them.
 */
function unwrapBrokenOfficeAppsOneDriveShortLink(parsed) {
  if (!parsed || parsed.hostname.toLowerCase() !== "view.officeapps.live.com") {
    return null;
  }

  const nestedSrc = parsed.searchParams.get("src");
  if (!nestedSrc) {
    return null;
  }

  const nestedParsed = parseHttpUrl(nestedSrc);
  if (!isConvertibleOneDrivePowerpointShortUrl(nestedParsed)) {
    return null;
  }

  return nestedParsed.toString();
}

/**
 * Rewrite Microsoft edit / legacy-broken Office Online wraps into a frameable embed URL.
 * Safe to call at save time and at render time. Leaves unrecognized URLs unchanged.
 * Modern 1drv.ms/p embeds from Share → Embed are kept as-is (do not Office Online wrap).
 */
export function normalizePowerpointEmbedUrl(url) {
  if (!url || typeof url !== "string") {
    return url;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return url;
  }

  const parsed = parseHttpUrl(trimmed);
  if (!parsed) {
    return trimmed;
  }

  const unwrappedShortLink = unwrapBrokenOfficeAppsOneDriveShortLink(parsed);
  if (unwrappedShortLink) {
    return unwrappedShortLink;
  }

  if (looksLikePowerpointOneDriveEditUrl(parsed)) {
    const reconstructedEmbed = buildOneDriveEmbedFromEditUrl(parsed);
    if (reconstructedEmbed) {
      return reconstructedEmbed;
    }

    const redeemed = decodeRedeemToUrl(parsed.searchParams.get("redeem"));
    if (redeemed) {
      const redeemedParsed = parseHttpUrl(redeemed);
      if (isConvertibleOneDrivePowerpointShortUrl(redeemedParsed)) {
        return redeemedParsed.toString();
      }
    }
  }

  return trimmed;
}

function isSharePointPowerpointEmbedUrl(parsed) {
  const host = parsed.hostname.toLowerCase();
  if (!host.endsWith(".sharepoint.com")) {
    return false;
  }

  const path = parsed.pathname.toLowerCase();

  // Classic SharePoint embed.aspx viewer
  if (path.includes("embed")) {
    return true;
  }

  // Office Online viewer layout page from Share → Embed (school tenants)
  if (path.includes("/doc.aspx") && hasExplicitEmbedMarker(parsed)) {
    return true;
  }

  // Anonymous guest-access embeds
  if (path.includes("guestaccess.aspx") && (hasExplicitEmbedMarker(parsed) || parsed.searchParams.has("authkey"))) {
    return true;
  }

  // PowerPoint sharing path (/:p:/…) with embed markers
  if (path.includes("/:p:/") && hasExplicitEmbedMarker(parsed)) {
    return true;
  }

  return false;
}

export function isRejectedThirdPartyEmbedUrl(url) {
  const parsed = parseHttpUrl(url);
  if (!parsed) {
    return true;
  }

  return REJECTED_HOST_PATTERNS.some((pattern) => pattern.test(parsed.hostname));
}

/**
 * Returns true when a URL looks like a Microsoft Office / OneDrive / SharePoint embed.
 * Used only during save validation — not when rendering stored content.
 */
export function isMicrosoftPowerpointEmbedUrl(url) {
  const parsed = parseHttpUrl(url);
  if (!parsed) {
    return false;
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname.toLowerCase();

  if (host === "view.officeapps.live.com") {
    return true;
  }

  if (isOneDriveConsumerEmbedHost(host) && path.startsWith("/embed")) {
    return true;
  }

  if (isConvertibleOneDrivePowerpointShortUrl(parsed)) {
    return true;
  }

  if (isSharePointPowerpointEmbedUrl(parsed)) {
    return true;
  }

  return false;
}

export function getPowerpointValidationMessage(rejectionReason) {
  switch (rejectionReason) {
    case "empty":
      return "Paste the full embed code from Share → Embed in PowerPoint for the web (save to OneDrive/SharePoint first).";
    case "third_party":
      return "This link looks like a different service. PowerPoint Box needs a Microsoft embed code.";
    case "onedrive_short_non_powerpoint":
      return "That OneDrive short link is not a PowerPoint file. PowerPoint Box needs a /p/ PowerPoint embed (or onedrive.live.com/embed / school SharePoint embed).";
    case "not_microsoft":
      return "This does not look like a Microsoft embed URL. Use Share → Embed in PowerPoint for the web (not Copy link, and not the desktop app). School OneDrive embeds often include Doc.aspx?action=embedview or onedrive.live.com/embed. Personal OneDrive Share → Embed codes (including 1drv.ms/p/) are accepted as-is.";
    case "unparseable":
      return "Paste the full iframe embed code from Share → Embed in PowerPoint for the web, not just a share link.";
    default:
      return "This does not appear to be a Microsoft PowerPoint embed. Save the file to OneDrive/SharePoint, open it in PowerPoint for the web, then copy Share → Embed.";
  }
}

/**
 * Validate teacher input when creating or updating a PowerPoint Box.
 * May rewrite onedrive.live.com/edit browser links into onedrive.live.com/embed,
 * and unwrap legacy Office Online wraps of 1drv.ms short links.
 * Existing Firestore URLs are still rendered defensively via normalizePowerpointEmbedUrl.
 */
export function validatePowerpointEmbedInput(input, options = {}) {
  const { existingSavedUrl = null } = options;

  if (!input || !String(input).trim()) {
    return {
      valid: false,
      url: null,
      rejectionReason: "empty",
      message: getPowerpointValidationMessage("empty"),
      wasNormalized: false,
    };
  }

  const extractedUrl = extractPowerpointEmbedUrl(input);
  if (!extractedUrl) {
    return {
      valid: false,
      url: null,
      rejectionReason: "unparseable",
      message: getPowerpointValidationMessage("unparseable"),
      wasNormalized: false,
    };
  }

  if (
    existingSavedUrl &&
    normalizeUrlForComparison(extractedUrl) === normalizeUrlForComparison(existingSavedUrl)
  ) {
    return {
      valid: true,
      url: existingSavedUrl,
      rejectionReason: null,
      message: null,
      wasNormalized: false,
    };
  }

  if (isRejectedThirdPartyEmbedUrl(extractedUrl)) {
    return {
      valid: false,
      url: extractedUrl,
      rejectionReason: "third_party",
      message: getPowerpointValidationMessage("third_party"),
      wasNormalized: false,
    };
  }

  const extractedParsed = parseHttpUrl(extractedUrl);
  if (
    extractedParsed &&
    isOneDriveShortLinkHost(extractedParsed.hostname.toLowerCase()) &&
    !isConvertibleOneDrivePowerpointShortUrl(extractedParsed)
  ) {
    const path = extractedParsed.pathname.toLowerCase();
    const rejectionReason = path.startsWith("/p/")
      ? "not_microsoft"
      : "onedrive_short_non_powerpoint";
    return {
      valid: false,
      url: extractedUrl,
      rejectionReason,
      message: getPowerpointValidationMessage(rejectionReason),
      wasNormalized: false,
    };
  }

  const normalizedUrl = normalizePowerpointEmbedUrl(extractedUrl);
  const wasNormalized =
    normalizeUrlForComparison(normalizedUrl) !== normalizeUrlForComparison(extractedUrl);

  if (!isMicrosoftPowerpointEmbedUrl(normalizedUrl)) {
    return {
      valid: false,
      url: extractedUrl,
      rejectionReason: "not_microsoft",
      message: getPowerpointValidationMessage("not_microsoft"),
      wasNormalized: false,
    };
  }

  return {
    valid: true,
    url: normalizedUrl,
    rejectionReason: null,
    message: null,
    wasNormalized,
  };
}

export function isValidPowerpointEmbedInput(input, options = {}) {
  return validatePowerpointEmbedInput(input, options).valid;
}

const SLIDE_TARGET_PARAM_PATTERN =
  /([?&])(?:wdSlideIndex|wdslideindex|WdSlideIndex|wdSlideId|wdSlideID|wdslideid)=[^&]*/gi;

/**
 * Clamp a teacher-entered slide number to a positive integer.
 */
export function normalizePowerpointSlideNumber(value, fallback = 1) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.floor(parsed);
}

/**
 * Remove Microsoft slide-target query params without re-encoding the rest of the URL.
 * URLSearchParams serialization can change nested src encodings (%20 → +) and break embeds.
 */
export function stripPowerpointSlideIndex(url) {
  if (!url || typeof url !== "string") {
    return url;
  }

  let next = url.replace(SLIDE_TARGET_PARAM_PATTERN, "$1");
  next = next.replace(/[?&]$/, "");
  next = next.replace(/\?&/, "?");
  next = next.replace(/&&+/g, "&");
  return next;
}

/**
 * Read wdSlideIndex from an embed URL when present.
 * Returns null when the URL has no usable slide index.
 */
export function extractPowerpointSlideIndex(url) {
  if (!url || typeof url !== "string") {
    return null;
  }

  const match = String(url).match(/[?&](?:wdSlideIndex|wdslideindex|WdSlideIndex)=([^&]*)/i);
  if (!match) {
    return null;
  }

  try {
    const raw = decodeURIComponent(match[1]);
    const slideNumber = normalizePowerpointSlideNumber(raw, 0);
    return slideNumber >= 1 ? slideNumber : null;
  } catch {
    return null;
  }
}

/**
 * Apply wdSlideIndex to a Microsoft PowerPoint embed URL for jump-to-slide remounts.
 * Always sets the param (including slide 1) so jumping back to slide 1 reloads correctly.
 * Preserves the original URL encoding aside from slide-target params.
 */
export function applyPowerpointSlideIndex(url, slideNumber) {
  if (!url || typeof url !== "string") {
    return url;
  }

  const normalizedSlide = normalizePowerpointSlideNumber(slideNumber, 1);
  const stripped = stripPowerpointSlideIndex(url);
  const separator = stripped.includes("?") ? "&" : "?";
  return `${stripped}${separator}wdSlideIndex=${normalizedSlide}`;
}

export function getPowerpointSlideStorageKey(classId, contentBoxId) {
  return `${classId}-${contentBoxId}-powerpointSlideNumber`;
}

/**
 * Prepare a validated embed URL + start slide for persistence.
 * Prefer the teacher-entered start slide; fall back to any wdSlideIndex already in the URL.
 */
export function preparePowerpointEmbedForSave(url, startSlideInput) {
  const extractedSlide = extractPowerpointSlideIndex(url);
  const hasExplicitStartSlide =
    startSlideInput !== undefined &&
    startSlideInput !== null &&
    String(startSlideInput).trim() !== "";

  const powerpointStartSlide = hasExplicitStartSlide
    ? normalizePowerpointSlideNumber(startSlideInput, 1)
    : extractedSlide || 1;

  return {
    url: stripPowerpointSlideIndex(url),
    powerpointStartSlide,
  };
}
